// src/views/Dashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import './Dashboard.css';
import './UnshippedOrders.css';
import './UnpaidInvoices.css';
import { showToast, greetUserToast } from '../utils/toast';

const API_BASE = (() => {
  try {
    const raw = (import.meta.env.VITE_API_URL || '').trim();
    if (!raw) return '';
    if (typeof window !== 'undefined') {
      const u = new URL(raw, window.location.origin);
      if (window.location.protocol === 'https:' && u.protocol === 'http:') return '';
      return u.origin;
    }
    return raw;
  } catch {
    return '';
  }
})();

/* ---------- user/role helpers ---------- */
function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}') || {};
  } catch {
    return {};
  }
}
function isClientUser(u) {
  const v = u.isclient ?? u.isClient ?? u.role ?? u.roleid;
  if (v === true) return true;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'client';
  }
  return false;
}
function getUserClientId(u) {
  const cid = u.clientid ?? u.clientId ?? u.client_id;
  return Number.isFinite(+cid) && +cid > 0 ? String(cid) : '';
}
/* -------------------------------------- */

const Dashboard = () => {
  const user = useMemo(() => getCurrentUser(), []);
  const IS_CLIENT = useMemo(() => isClientUser(user), [user]);
  const LOCKED_CLIENT_ID = useMemo(() => getUserClientId(user), [user]);

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  const [sortFieldOrders, setSortFieldOrders] = useState('orderNumber');
  const [sortOrderOrders, setSortOrderOrders] = useState('ASC');

  const [sortFieldInvoices, setSortFieldInvoices] = useState('invoiceId');
  const [sortOrderInvoices, setSortOrderInvoices] = useState('ASC');

  const [revenue, setRevenue] = useState([]);
  const [loadingRevenue, setLoadingRevenue] = useState(true);

  // Cache theme text color once to avoid repeated getComputedStyle during chart renders
  const [themeTextColor, setThemeTextColor] = useState('#ffffff');
  useEffect(() => {
    try {
      const c = getComputedStyle(document.documentElement)
        .getPropertyValue('--text-color')
        .trim();
      if (c) setThemeTextColor(c);
    } catch {}
  }, []);
  // (removed ResizeObserver gating to ensure chart renders immediately)

  const monthLabel = (d) =>
    d.toLocaleString(undefined, { month: 'short', year: 'numeric' });

  // Fetch revenue (internal users only)
  useEffect(() => {
    if (IS_CLIENT) {
      // Clients do not see revenue chart
      setRevenue([]);
      setLoadingRevenue(false);
      return;
    }

    const fetchRevenue = async () => {
      setLoadingRevenue(true);
      try {
        const res = await fetch(`${API_BASE}/api/reports/revenue/monthly?months=6`);
        const data = await res.json(); // [{ monthStart: "YYYY-MM-01", revenue: number }, ...]
        const map = new Map(
          (Array.isArray(data) ? data : []).map((r) => [
            r.monthStart,
            Number(r.revenue) || 0
          ])
        );

        const now = new Date();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            '0'
          )}-01`;
          months.push({
            key,
            label: monthLabel(d),
            total: map.get(key) ?? 0
          });
        }
        setRevenue(months);
      } catch (e) {
        console.error('Failed to fetch monthly revenue:', e);
        setRevenue([]);
      } finally {
        setLoadingRevenue(false);
      }
    };
    fetchRevenue();
  }, [IS_CLIENT]);

  // Unshipped orders (filtered for clients)
  useEffect(() => {
    const fetchOrders = async () => {
      setLoadingOrders(true);
      try {
        const params = new URLSearchParams({
          sortField: sortFieldOrders,
          sortOrder: sortOrderOrders
        });
        if (IS_CLIENT && LOCKED_CLIENT_ID) {
          params.set('clientid', LOCKED_CLIENT_ID);
        }
        const response = await fetch(`${API_BASE}/api/unshipped_orders?${params.toString()}`);
        const data = await response.json();
        setOrders(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch unshipped orders:', error);
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };
    fetchOrders();
  }, [sortFieldOrders, sortOrderOrders, IS_CLIENT, LOCKED_CLIENT_ID]);

  // Unpaid invoices (filtered for clients)
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const params = new URLSearchParams({
          sortField: sortFieldInvoices,
          sortOrder: sortOrderInvoices
        });
        if (IS_CLIENT && LOCKED_CLIENT_ID) {
          params.set('clientid', LOCKED_CLIENT_ID);
        }
        const response = await fetch(`${API_BASE}/api/unpaid_invoices?${params.toString()}`);
        const data = await response.json();
        setInvoices(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to fetch unpaid invoices:', error);
        setInvoices([]);
      } finally {
        setLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [sortFieldInvoices, sortOrderInvoices, IS_CLIENT, LOCKED_CLIENT_ID]);

  const handleSortOrders = (field) => {
    if (sortFieldOrders === field) {
      setSortOrderOrders(sortOrderOrders === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortFieldOrders(field);
      setSortOrderOrders('ASC');
    }
  };

  const handleSortInvoices = (field) => {
    if (sortFieldInvoices === field) {
      setSortOrderInvoices(sortOrderInvoices === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortFieldInvoices(field);
      setSortOrderInvoices('ASC');
    }
  };

  const renderSortHeader = (label, field, currentField, currentOrder, onClick) => (
    <th onClick={() => onClick(field)} className={currentField === field ? 'active-sort' : ''}>
      {label} {currentField === field ? (currentOrder === 'ASC' ? '▲' : '▼') : ''}
    </th>
  );

  const barColors = [
    '#ff6200', // orange
    '#0088FE', // blue
    '#00C49F', // teal
    '#FFBB28', // gold
    '#FF8042', // orange-red
    '#A28EFF', // purple
  ];

  // ApexCharts options/series for revenue chart
  const apexCategories = useMemo(() => revenue.map(r => r.label), [revenue]);
  const apexSeries = useMemo(
    () => [
      {
        name: 'Revenue',
        data: revenue.map(r => Number(r.total) || 0),
      },
    ],
    [revenue]
  );
  const apexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        animations: { enabled: false },
        toolbar: { show: false },
        background: 'transparent',
        foreColor: themeTextColor,
      },
      grid: { strokeDashArray: 3, padding: { top: 10 } },
      colors: barColors,
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 4,
          columnWidth: '45%',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -14,
        style: { colors: [themeTextColor], fontSize: '12px', fontWeight: 500 },
        formatter: (val) =>
          `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      },
      xaxis: {
        categories: apexCategories,
        labels: {
          style: {
            colors: apexCategories.map(() => themeTextColor),
            fontSize: '12px',
          },
        },
        axisBorder: { color: 'rgba(128,128,128,0.3)' },
        axisTicks: { color: 'rgba(128,128,128,0.3)' },
      },
      yaxis: {
        labels: {
          style: { colors: [themeTextColor], fontSize: '12px' },
          formatter: (val) => `$${Number(val || 0).toLocaleString()}`,
        },
      },
      tooltip: {
        y: {
          formatter: (val) =>
            `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          title: { formatter: () => 'Revenue' },
        },
      },
      legend: { show: false },
      theme: { mode: 'light' },
    }),
    [apexCategories, themeTextColor]
  );

  // One-time greeting after successful login
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem('justLoggedIn');
      if (flag === '1') {
        greetUserToast(user);
        sessionStorage.removeItem('justLoggedIn');
      }
    } catch {}
  }, [user]);

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-row">
        <div className="dashboard-card">
          <h2>Unshipped Orders</h2>
          <div className={`fade-wrapper ${loadingOrders ? 'fade-out' : 'fade-in'}`}>
            <table className="orders-table">
              <thead>
                <tr>
                  {renderSortHeader('Order Number', 'orderNumber', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                  {renderSortHeader('Created Date', 'createdDate', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                  {renderSortHeader('Client Name', 'clientName', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                  {renderSortHeader('Status', 'statusName', sortFieldOrders, sortOrderOrders, handleSortOrders)}
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <tr key={order.orderNumber}>
                      <td>{order.orderNumber}</td>
                      <td>{order.createdDate ? new Date(order.createdDate).toLocaleString() : ''}</td>
                      <td>{order.clientName}</td>
                      <td>{order.statusName}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No unshipped orders found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-card">
          <h2>Unpaid Invoices</h2>
          <div className={`fade-wrapper ${loadingInvoices ? 'fade-out' : 'fade-in'}`}>
            <table className="invoices-table">
              <thead>
                <tr>
                  {renderSortHeader('Invoice ID', 'invoiceId', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                  {renderSortHeader('Invoice Date', 'invoiceDate', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                  {renderSortHeader('Client Name', 'clientName', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                  {renderSortHeader('Amount Due', 'amountDue', sortFieldInvoices, sortOrderInvoices, handleSortInvoices)}
                </tr>
              </thead>
              <tbody>
                {invoices.length > 0 ? (
                  invoices.map((inv) => (
                    <tr key={inv.invoiceId}>
                      <td>{inv.invoiceId}</td>
                      <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleString() : ''}</td>
                      <td>{inv.clientName}</td>
                      <td>${Number(inv.amountDue ?? 0).toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">No unpaid invoices found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {!IS_CLIENT && (
        <div className="dashboard-row">
          <div className="dashboard-card dashboard-card-wide">
            <h2>Revenue (Last 6 Months)</h2>
            <div className={`fade-wrapper revenue-wrap ${loadingRevenue ? 'fade-out' : 'fade-in'}`}>
              {revenue.length ? (
                <ReactApexChart
                  type="bar"
                  options={apexOptions}
                  series={apexSeries}
                  width="100%"
                  height="100%"
                />
              ) : (
                <div className="loading">No revenue data.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
